import {
  SOCIAL_UPDATES_PER_MONTH,
  SocialEstimateResponse,
  SocialTrackerCadence,
  SocialUniverseChannel,
  SocialUniverseItem,
  SocialUniverseRunItem,
} from '@digital-twins/shared-types'
import type { TFunction } from 'i18next'

import { intlLocaleForUi } from '@/i18n'

/**
 * Social listening credits: pure math, formatting and copy selection for the
 * credits UI (credits UI plan Step 2, design spec S1 to S9). The server owns
 * every money fact (holds, charges, funds state, cooldowns); this module only
 * formats them, multiplies a per-update figure by updates per month and picks
 * which copy a surface shows. Never prices or sizes a Topic: the dialog reads
 * its posts and ceilings from `POST /social-listening/universes/estimate`
 * (`useSocialEstimate`).
 */

/** Days of settled history an "about N credits a month" figure averages (E10). */
export const SOCIAL_OBSERVED_WINDOW_DAYS = 7
/** Debounce for the wizard estimate request on channel / cap / cadence change (E2). */
export const SOCIAL_ESTIMATE_DEBOUNCE_MS = 300
/** At most one profile refresh per this window after holds and settlements (E1). */
export const SOCIAL_CREDITS_REFRESH_DEBOUNCE_MS = 5000

const DAY_MS = 24 * 60 * 60 * 1000

/** Rounds away float noise (34 x 30.000000000000004) before a ceil, as shared-types does. */
function roundForStep(value: number): number {
  return Math.round(value * 1e6) / 1e6
}

export interface FormatSocialCreditsOptions {
  /** Ceilings and balances: whole numbers, rounded up. */
  ceiling?: boolean
}

/**
 * Locale-aware credit amount (E9): ceilings whole (rounded up); spent amounts
 * at most 1 decimal at 1 or more and at most 2 below 1, trailing zeros
 * dropped (en 12.4, es 12,4, 0.13, 148). Pass the raw number as i18next
 * `count` and this string as `value`. Not `formatCreditAmount` (not locale
 * aware).
 */
export function formatSocialCredits(
  value: number | null | undefined,
  lng: string | null | undefined,
  options: FormatSocialCreditsOptions = {},
): string {
  const safe = typeof value === 'number' && Number.isFinite(value) ? value : 0
  const locale = intlLocaleForUi(lng)
  if (options.ceiling) {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
      Math.ceil(roundForStep(safe)),
    )
  }
  const maximumFractionDigits = Math.abs(safe) >= 1 ? 1 : 2
  return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(safe)
}

/** The price a run's offer shows: whole credits rounded down, or the exact amount under 1 so it never reads 0. */
export function socialOfferCredits(credits: number): number {
  return credits >= 1 ? Math.floor(credits) : credits
}

/**
 * The month line ("This month: 1 credit, 63 posts added."): each count is
 * pluralised on the number shown, so 1 reads singular in every locale.
 */
export function socialMonthLine(
  t: TFunction<'app'>,
  lng: string | null | undefined,
  credits: number | null | undefined,
  posts: number | null | undefined,
): string {
  const safeCredits = typeof credits === 'number' && Number.isFinite(credits) ? credits : 0
  const safePosts = typeof posts === 'number' && Number.isFinite(posts) ? posts : 0
  // The plural follows the shown amount (1.04 shows "1", so it reads "1 credit").
  const step = Math.abs(safeCredits) >= 1 ? 10 : 100
  const shownCredits = Math.round(safeCredits * step) / step
  return t('socialListening.credits.month', {
    credits: t('socialListening.credits.run.charged', {
      count: shownCredits,
      value: formatSocialCredits(safeCredits, lng),
    }),
    posts: t('socialListening.credits.monthPosts', {
      count: safePosts,
      value: new Intl.NumberFormat(intlLocaleForUi(lng)).format(safePosts),
    }),
  })
}

/** Scheduled updates in a month (daily 30, weekly 52 / 12); null for on demand. */
export function socialUpdatesPerMonth(cadence: SocialTrackerCadence): number | null {
  if (cadence === 'on_demand') {
    return null
  }
  return SOCIAL_UPDATES_PER_MONTH[cadence]
}

export interface TopicCreditsFromEstimateInput {
  /** The server estimate; null while unknown. */
  estimate: Pick<SocialEstimateResponse, 'maxCreditsPerUpdate' | 'maxCreditsPerMonth'> | null
  cadence: SocialTrackerCadence
  /** The Topic's runs (Activity); empty on create. */
  recentRuns: Array<SocialUniverseRunItem>
  /** Channels after the edit: an observed figure needs history on every one. */
  platforms: Array<SocialUniverseRunItem['platform']>
  runPostLimit: number
  /** The cap the observed runs ran under (the saved Topic's); null on create. */
  savedRunPostLimit: number | null
  /** Clock for the 7-day window; defaults to now. */
  now?: number
}

export interface TopicCreditsEstimate {
  /** Whole credits ("up to"). */
  maxPerUpdate: number
  /** Whole credits ("up to"); null for on demand. */
  maxPerMonth: number | null
  /** Mean charged credits per update over the window; null without enough history. */
  observedPerUpdate: number | null
  /** `observedPerUpdate` x updates per month, never above `maxPerMonth`; null without history or on demand. */
  observedPerMonth: number | null
}

function runEndMs(run: SocialUniverseRunItem): number | null {
  const stamp = run.finishedAt ?? run.startedAt
  if (!stamp) {
    return null
  }
  const ms = Date.parse(stamp)
  return Number.isFinite(ms) ? ms : null
}

function isSettledLaterUpdate(run: SocialUniverseRunItem): boolean {
  return (
    run.billingStatus === 'settled' &&
    typeof run.creditsCharged === 'number' &&
    Number.isFinite(run.creditsCharged) &&
    // First updates read the whole range: they say nothing about later ones.
    run.depthDays === null
  )
}

/**
 * Mean settled credits per update over the last SOCIAL_OBSERVED_WINDOW_DAYS
 * (E10): per channel, the mean charge of its settled later updates in the
 * window, summed across channels (includes the cost-plus minimum, so a quiet
 * Topic never reads "about 0"). Null unless the cap is unchanged, the Topic
 * has settled history at least a window old, and every channel has a settled
 * update in the window.
 */
export function observedCreditsPerUpdate(input: {
  recentRuns: Array<SocialUniverseRunItem>
  platforms: Array<SocialUniverseRunItem['platform']>
  runPostLimit: number
  savedRunPostLimit: number | null
  now?: number
}): number | null {
  if (input.platforms.length === 0) {
    return null
  }
  if (input.savedRunPostLimit === null || input.savedRunPostLimit !== input.runPostLimit) {
    return null
  }
  const now = input.now ?? Date.now()
  const windowStart = now - SOCIAL_OBSERVED_WINDOW_DAYS * DAY_MS
  const settled = input.recentRuns.filter(
    (run) => run.billingStatus === 'settled' && runEndMs(run) !== null,
  )
  const oldest = settled.reduce<number | null>((min, run) => {
    const end = runEndMs(run) as number
    return min === null || end < min ? end : min
  }, null)
  if (oldest === null || oldest > windowStart) {
    return null
  }
  let total = 0
  for (const platform of input.platforms) {
    const charges = input.recentRuns
      .filter((run) => {
        const end = runEndMs(run)
        return (
          run.platform === platform &&
          isSettledLaterUpdate(run) &&
          end !== null &&
          end >= windowStart &&
          end <= now
        )
      })
      .map((run) => run.creditsCharged as number)
    if (charges.length === 0) {
      return null
    }
    total += charges.reduce((sum, value) => sum + value, 0) / charges.length
  }
  return roundForStep(total)
}

/**
 * The dialog's credit figures: "up to" per update and per month exactly as
 * the server estimate states them, plus "about N a month" once the Topic has
 * a week of settled history under the same cap (charged credits, never a
 * price). Null while the estimate is unknown.
 */
export function topicCreditsFromEstimate(
  input: TopicCreditsFromEstimateInput,
): TopicCreditsEstimate | null {
  if (input.estimate === null) {
    return null
  }
  const { maxCreditsPerUpdate: maxPerUpdate, maxCreditsPerMonth: maxPerMonth } = input.estimate
  const observedPerUpdate = observedCreditsPerUpdate(input)
  const updates = socialUpdatesPerMonth(input.cadence)
  let observedPerMonth: number | null = null
  if (observedPerUpdate !== null && updates !== null) {
    const month = roundForStep(observedPerUpdate * updates)
    observedPerMonth = maxPerMonth === null ? month : Math.min(month, maxPerMonth)
  }
  return { maxPerUpdate, maxPerMonth, observedPerUpdate, observedPerMonth }
}

export type SocialTopicCreditBannerKind = 'auto' | 'resume' | 'ops' | 'low' | 'insufficient'

export interface SocialTopicCreditBanner {
  kind: SocialTopicCreditBannerKind | null
}

type BannerChannel = Pick<SocialUniverseChannel, 'status' | 'pauseReason' | 'autoResumeEndsAt'>

type BannerUniverse = Pick<
  SocialUniverseItem,
  'cadence' | 'fundsState' | 'autoResumeEndsAt'
> & { channels: ReadonlyArray<BannerChannel> }

function isPausedFor(channel: BannerChannel, reason: 'out_of_credits' | 'ops'): boolean {
  return channel.status === 'paused' && channel.pauseReason === reason
}

function earliestIso(values: Array<string | null | undefined>): number | null {
  let earliest: number | null = null
  for (const value of values) {
    if (!value) {
      continue
    }
    const ms = Date.parse(value)
    if (Number.isFinite(ms) && (earliest === null || ms < earliest)) {
      earliest = ms
    }
  }
  return earliest
}

/**
 * Which credit notice a Topic page shows (C5, E3): `auto` / `resume` when any
 * channel is paused for credits (auto while the Topic-level
 * `autoResumeEndsAt` is still ahead on a scheduled cadence; on demand always
 * `resume`); `ops` when any channel is paused for maintenance; else the funds
 * line (`insufficient`, `low`).
 */
export function topicCreditBanner(
  universe: BannerUniverse,
  now: number = Date.now(),
): SocialTopicCreditBanner {
  const creditPaused = universe.channels.filter((channel) => isPausedFor(channel, 'out_of_credits'))
  if (creditPaused.length > 0) {
    if (universe.cadence === 'on_demand') {
      return { kind: 'resume' }
    }
    const endsAt =
      earliestIso([universe.autoResumeEndsAt]) ??
      earliestIso(creditPaused.map((channel) => channel.autoResumeEndsAt))
    return { kind: endsAt !== null && endsAt > now ? 'auto' : 'resume' }
  }
  if (universe.channels.some((channel) => isPausedFor(channel, 'ops'))) {
    return { kind: 'ops' }
  }
  if (universe.fundsState === 'insufficient') {
    return { kind: 'insufficient' }
  }
  if (universe.fundsState === 'low') {
    return { kind: 'low' }
  }
  return { kind: null }
}

export interface SocialRunCreditLines {
  /** After "N added · " on a done row ("3.7 credits"); null when nothing was charged. */
  headlineSuffix: string | null
  /** Replaces the row summary (out of credits, paused for maintenance); null otherwise. */
  summaryOverride: string | null
  /** A small muted line under the row (held, nothing charged, unpaid); null when none. */
  statusLine: string | null
  /** Credit lines inside the collapsed Details (set aside, charged, returned). */
  details: Array<string>
}

export interface SocialRunCreditCopy {
  t: TFunction<'app'>
  lng: string | null | undefined
}

type CreditRun = Pick<
  SocialUniverseRunItem,
  'status' | 'failureReason' | 'billingStatus' | 'creditsHeld' | 'creditsCharged'
>

/**
 * Credit copy for one Activity / drawer run row (S6, E4). Never mentions the
 * charge basis, USD or millicredits. A run recorded `free` (claimed before
 * charging) shows no amounts.
 */
export function runCreditLines(run: CreditRun, copy: SocialRunCreditCopy): SocialRunCreditLines {
  const { t, lng } = copy
  const lines: SocialRunCreditLines = {
    headlineSuffix: null,
    summaryOverride: null,
    statusLine: null,
    details: [],
  }
  if (run.failureReason === 'out_of_credits') {
    lines.summaryOverride = t('socialListening.topicActivity.reasonOutOfCredits')
    return lines
  }
  if (run.failureReason === 'paused_by_ops') {
    lines.summaryOverride = t('socialListening.topicActivity.reasonPausedByOps')
    return lines
  }
  if (run.billingStatus === 'free') {
    return lines
  }
  const held = typeof run.creditsHeld === 'number' ? run.creditsHeld : null
  const charged = typeof run.creditsCharged === 'number' ? run.creditsCharged : null
  switch (run.billingStatus) {
    case 'held': {
      if (held !== null && held > 0) {
        lines.statusLine = t('socialListening.credits.run.held', {
          count: held,
          value: formatSocialCredits(held, lng),
        })
      }
      return lines
    }
    case 'settled': {
      if (charged !== null && charged > 0) {
        lines.headlineSuffix = t('socialListening.credits.run.charged', {
          count: charged,
          value: formatSocialCredits(charged, lng),
        })
      }
      // An accepted offer charges past the hold: nothing came back to report.
      if (held !== null && held > 0 && charged !== null && charged <= held) {
        lines.details.push(
          t('socialListening.credits.run.details', {
            held: formatSocialCredits(held, lng),
            charged: formatSocialCredits(charged, lng),
          }),
        )
      }
      return lines
    }
    case 'unpaid': {
      lines.statusLine = t('socialListening.credits.run.unpaid')
      return lines
    }
    case 'released': {
      lines.statusLine = t('socialListening.credits.run.nothingCharged')
      return lines
    }
    default: {
      // Failed runs and runs stopped at the spend guard never charge.
      if (run.status === 'failed' || run.failureReason === 'budget') {
        lines.statusLine = t('socialListening.credits.run.nothingCharged')
      }
      return lines
    }
  }
}

/** Earliest future moment among the given ISO stamps; null when none is ahead of `now`. */
export function earliestFutureMs(
  values: ReadonlyArray<string | null | undefined>,
  now: number = Date.now(),
): number | null {
  let earliest: number | null = null
  for (const value of values) {
    if (!value) {
      continue
    }
    const ms = Date.parse(value)
    if (Number.isFinite(ms) && ms > now && (earliest === null || ms < earliest)) {
      earliest = ms
    }
  }
  return earliest
}

/**
 * "in 40 minutes" for a cooldown (`credits.update.cooldown`), via
 * `Intl.RelativeTimeFormat` in the UI locale: minutes under an hour, hours
 * under a day, days after; at least one minute.
 */
export function formatSocialRelativeTime(
  targetMs: number,
  lng: string | null | undefined,
  now: number = Date.now(),
): string {
  const formatter = new Intl.RelativeTimeFormat(intlLocaleForUi(lng), { numeric: 'auto' })
  const diffMinutes = Math.max(1, Math.ceil((targetMs - now) / 60_000))
  if (diffMinutes < 60) {
    return formatter.format(diffMinutes, 'minute')
  }
  const diffHours = Math.ceil(diffMinutes / 60)
  if (diffHours < 24) {
    return formatter.format(diffHours, 'hour')
  }
  return formatter.format(Math.ceil(diffHours / 24), 'day')
}

/**
 * Whole credits of the next runs' imports (server estimate): each channel
 * with a first import or added imports counts its `importCredits`; null when
 * no channel imports anything (the row is hidden).
 */
export function socialImportCredits(
  estimate: Pick<SocialEstimateResponse, 'channels'> &
    Partial<Pick<SocialEstimateResponse, 'maxCreditsImports'>>,
): number | null {
  // The server rounds the total once; summing whole credits per channel rounds up each.
  if (estimate.maxCreditsImports !== undefined) {
    return estimate.maxCreditsImports
  }
  const importing = estimate.channels.filter(
    (channel) => channel.nextRun.firstImport !== null || channel.nextRun.addedImports.length > 0,
  )
  if (importing.length === 0) {
    return null
  }
  return importing.reduce((sum, channel) => sum + channel.nextRun.importCredits, 0)
}
